import React, { memo, useCallback, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { RootState } from '../../../store/store';
import { Button, Chip, Tabs, Tab } from '@mui/material';
import { ExpandMore } from '@mui/icons-material';
import CustomTabPanel from '../../../components/commonComponent/CustomTabPanel';
import FilterData from './FilterData';
import styles from './plansetup.module.scss';

interface PlanProps {
  handleClick: () => void;
}

const PlanSetup: React.FC<PlanProps> = ({ handleClick }) => {
  const [value, setValue] = useState<number>(0);
  const [expanded, setExpanded] = useState<boolean>(true);

  // ------------------------------
  // Selectors
  // ------------------------------
  const { sectors, regions, sites } = useSelector(
    (state: RootState) => state.sectorRegionAllSitesAdminConfiguration
  );

  const { params } = useSelector((state: RootState) => state.adminConfigurationParams);

  // ------------------------------
  // Memoized IDs and arrays
  // ------------------------------
  const regionId = useMemo(() => params?.regionid || 0, [params?.regionid]);

  // If sitelist exists, map to string
  const siteidlist = useMemo(() => {
    return Array.isArray(params?.sitelist) ? params.sitelist.map(String) : [];
  }, [params?.sitelist]);

  // Filter out the user’s sites
  const siteItems = useMemo(() => {
    return sites
      .filter((site) => siteidlist.includes(String(site.sitecode)))
      .map((site) => ({
        sitecode: site.sitecode,
        label: site.sitename,
      }));
  }, [sites, siteidlist]);

  // Memoize sector
  const sectorcode = useMemo(() => params?.sector || '', [params?.sector]);
  const sectorName = useMemo(() => {
    return sectors?.find((s) => s.sectorcode === sectorcode)?.sectorname || '';
  }, [sectors, sectorcode]);

  // Memoize region(s)
  const regionslist = useMemo(() => {
    return regions
      .filter((region) =>
        Array.isArray(regionId)
          ? regionId.includes(region.regionid)
          : regionId === region.regionid
      )
      .map((region) => ({
        id: region.regionid,
        label: region.regionname,
      }));
  }, [regions, regionId]);

  // ------------------------------
  // Handlers
  // ------------------------------
  const handleChangeTab = useCallback(
    (_event: React.SyntheticEvent, newValue: number) => {
      setValue(newValue);
    },
    []
  );

  const handleToggleChange = useCallback(() => {
    setExpanded((prev) => !prev);
  }, []);

  // ------------------------------
  // Render
  // ------------------------------
  return (
    <div className="white-box mb-12">
      <div
        className={`flex justify-content-between align-items-center ${
          expanded ? styles.tabbingDataWrap : styles.tabbingDataWrapMinimize
        }`}
      >
        {expanded ? (
          <Tabs
            value={value}
            onChange={handleChangeTab}
            aria-label="Tabs"
            className={expanded ? 'tabs-wrapper' : 'tabs-wrapper colapsed'}
          >
            <Tab
              label="Filter"
              id={`simple-tabpanel-0`}
              aria-labelledby={`simple-tab-0`}
              disableRipple
            />
          </Tabs>
        ) : (
          <div className="flex align-items-center">
            {/* Sector Display */}
            <div className={`selecte-sites flex align-items-center justify-content-start ${styles.selectedSites}`}>
              <span className={styles.dateLabel}>Sector:</span>
              <span className={styles.chipData}>{sectorName}</span>
            </div>

            {/* Region Display */}
            <div className={`selecte-sites flex align-items-center justify-content-start ${styles.selectedSites}`}>
              <span className={styles.dateLabel}>Region:</span>
              <span className={styles.chipData}>{regionslist[0]?.label}</span>
            </div>

            {/* Site Display */}
            <div className={`selecte-sites flex align-items-center justify-content-start ${styles.selectedSites}`}>
              <span className={styles.dateLabel}>Select Site:</span>
              <span className={styles.chipData}>
                {!params?.isallsite ? (
                  siteItems.length > 0 &&
                  siteItems.map((item) => (
                    <Chip key={item.sitecode} label={item.label} className={styles.chip} />
                  ))
                ) : (
                  <Chip key="ALL_SITES" label="All Sites" className={styles.chip} />
                )}
              </span>
            </div>
          </div>
        )}

        <Button onClick={handleToggleChange} className={`expend-btn ${expanded ? 'active' : ''}`} disableRipple>
          {expanded ? 'Minimize Section' : 'Expand'}
          <span>
            <ExpandMore />
          </span>
        </Button>
      </div>

      <div className={`inner-contant ${!expanded ? 'd-none' : ''}`}>
        <CustomTabPanel value={value} index={0}>
          <FilterData handleClick={handleClick} handleMenuChange={handleToggleChange} />
        </CustomTabPanel>
      </div>
    </div>
  );
};

export default memo(PlanSetup);
